import { FC, useEffect, useState } from 'react'
import { IoMdArrowBack } from 'react-icons/io'

import HeadingView from '../HeadingView/HeadingView'
import BackButton from '../BackButton/BackButton'
import TrackItem from '../TrackItem/TrackItem'
import { NewCustomerType } from '../../../types'
import { useGetUserQuery, User } from '../../../generated/graphql'
import CustomText from '../../CustomText'

type Track_Item = {
  count: number
  title: string
  description: string
  tertiary?: string
  isActive: boolean
  passedStepper: boolean
}

const TRACK_STEPS: Track_Item[] = [
  {
    count: 1,
    title: 'Invite delivered',
    description: 'Sam received the onboarding link by email.',
    isActive: false,
    passedStepper: false
  },
  {
    count: 2,
    title: 'Onboarding opened',
    description: 'Sam has opened the link and started.',
    isActive: false,
    passedStepper: false
  },
  {
    count: 3,
    title: 'Credit check pending',
    description: "What's next: Waiting on Sam to confirm a few details",
    tertiary: 'Affordability check in progress.',
    isActive: false,
    passedStepper: false
  },
  {
    count: 4,
    title: 'Banking details pending',
    description: 'New owner to capture banking details.',
    isActive: false,
    passedStepper: false
  },

  {
    count: 5,
    title: 'Debit order date selection',
    description: 'New owner picks their preferred debit date.',
    isActive: false,
    passedStepper: false
  },
  {
    count: 6,
    title: 'ID verification pending',
    description: 'ID document upload and verification.',
    isActive: false,
    passedStepper: false
  },
  {
    count: 7,
    title: 'Handover switch scheduled',
    description: 'We lock in property inspection, billing switch and more ...',
    isActive: false,
    passedStepper: false
  },
  {
    count: 8,
    title: 'Handover complete',
    description: `All done. The system is officially in the new owner's hands.`,
    isActive: false,
    passedStepper: false
  }
]

type TrackHandoverProps = {
  stepCount: number
  totalStep: number
  title: string
  description: string
  handleBack: () => void
  newCustomer?: NewCustomerType
}

export const TrackHandover: FC<TrackHandoverProps> = ({
  stepCount,
  totalStep,
  title,
  description,
  handleBack,
  newCustomer
}: TrackHandoverProps) => {
  const [customer, setCustomer] = useState<NewCustomerType>()
  const [loadingProperties, setLoadingProperties] = useState<boolean>(true)
  const [user, setUser] = useState<User>()
  // Get user
  const { refetch: getUser } = useGetUserQuery({
    skip: true
  })

  const [activeStep, setActiveStep] = useState<Track_Item>(TRACK_STEPS[0])

  useEffect(() => {
    if (newCustomer) {
      setCustomer(newCustomer)
    }
  }, [newCustomer])

  useEffect(() => {
    const retrieveUserProperties = async (newCustomer: NewCustomerType) => {
      setLoadingProperties(true)

      const { data: userData, loading: loadingUser } = await getUser({
        email: newCustomer?.email,
        public: true
      })
      const currentUser = userData?.getUser

      if (currentUser?.__typename === 'ErrorResponse') {
        setLoadingProperties(false)
        throw Error('An error occured when trying to fetch user from getUser')
      }
      if (currentUser?.__typename === 'User') {
        const existingUserProduct = currentUser?.properties?.find(
          (item) => item?.recordId === newCustomer?.userRecordId
        )

        if (existingUserProduct?.onboardingProgressCompletedStep === 4) {
          setActiveStep(TRACK_STEPS[1])
        }

        if (existingUserProduct?.entityType) {
          setActiveStep(TRACK_STEPS[2])
        }

        if (existingUserProduct?.creditCheckPassed !== null) {
          setActiveStep(TRACK_STEPS[3])
        }

        if (existingUserProduct?.bankAccountNumber || existingUserProduct?.bankAccountType) {
          setActiveStep(TRACK_STEPS[5])
        }

        if (existingUserProduct?.idPassportDocumentS3Key) {
          setActiveStep(TRACK_STEPS[6])
        }

        if (existingUserProduct?.orderStatus?.toLocaleLowerCase() === 'installation commissioned') {
          setActiveStep(TRACK_STEPS[6])
        }

        //   if (existingUserProduct?.de || existingUserProduct?.bankAccountType) {
        //   setActiveStep(TRACK_STEPS[4])
        // }

        console.log(`existingUserProduct: `, existingUserProduct)

        setLoadingProperties(false)
        setUser(currentUser)
      }
    }
    !user && customer && retrieveUserProperties(customer)
  }, [customer, user, getUser])

  console.log(`user: `, user)

  return (
    <div className={`w-full h-auto flex flex-col gap-y-4`}>
      <HeadingView
        stepCount={stepCount}
        totalStep={totalStep}
        title={title}
        description={description}
      />

      <div className="w-full h-auto p-12 flex flex-col bg-white rounded-[32px] shadow-sm border-solid border-[1.5px] border-[#e4e4e4]">
        {!loadingProperties &&
          TRACK_STEPS?.map((item, idx) => (
            <TrackItem
              key={`track-item-${idx + 1}`}
              isFirstItem={idx === 0}
              isLastItem={TRACK_STEPS?.length === idx + 1}
              label={item?.title}
              description={item?.description.replace(`Sam`, `${newCustomer?.firstName}`)}
              tertiary={item?.tertiary}
              count={item.count.toString()}
              isActive={item.count === activeStep?.count}
              passedStepper={item.count < activeStep.count}
              handleClick={() => null}
              disableButton
            />
          ))}
        {loadingProperties && (
          <div className="w-full h-auto flex flex-row justify-between items-center">
            <CustomText
              textLabel={`Fetching new customer details ...`}
              fontWeight={`font-semibold`}
              fontSize="text-[16px]"
              fontColor={`text-black`}
            />
          </div>
        )}
      </div>

      <div className="w-fit h-fit flex flex-row justify-between">
        <BackButton label="Back" handleClick={handleBack} isBack={false}>
          <IoMdArrowBack className="w-[20px] h-[20px]" color="#000000" />
        </BackButton>
      </div>
    </div>
  )
}

export default TrackHandover
